import type * as React from 'react';

import { CustomizationThemeMode } from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import type { ContentThemeMode } from '@gitbook/app-database';
import { Icon } from '@gitbook/spine-icons';
import {
    ActionableContainer,
    Description,
    Group,
    ImagePreview,
    ImagePreviewIcon,
    ImagePreviewRemoveButton,
    Label,
    ProgressiveImage,
} from '@gitbook/spine-react';
import { isUrl } from '@gitbook/util-url';

import { getImageResizedURL, useUploadFile } from '../../../../utils';
import type { SitesCustomizationPanelProps } from '../../types';
import { setThemedURL } from '../../utils';

/**
 * Settings to configure the footer logo.
 */
export function FooterLogoSettings(props: SitesCustomizationPanelProps): React.ReactElement {
    return (
        <ActionableContainer>
            <Group>
                <Label>Logo</Label>
                <Description>
                    Display a custom logo in footer.
                    <br />
                    Recommended width: 600px or wider.
                </Description>
            </Group>
            <div className="flex gap-4">
                {Object.values(CustomizationThemeMode).map((mode) => (
                    <LogoPreview key={mode} {...props} themeMode={mode} />
                ))}
            </div>
        </ActionableContainer>
    );
}
/**
 * Handles the preview and management of the logo for a given theme mode.
 */
function LogoPreview(
    props: SitesCustomizationPanelProps & { themeMode: ContentThemeMode }
): React.ReactElement {
    const { settings, setSettings, organizationId, site, themeMode } = props;

    const footerLogoResizedURI = settings.footer.logo?.[themeMode]
        ? getImageResizedURL(settings.footer.logo[themeMode], {
              width: 200,
              height: 100,
              fit: 'contain',
          })
        : undefined;
    const promptAndUpload = useUploadFile();

    const onPressUpload = async () => {
        const metadata = await promptAndUpload(
            {
                kind: 'site_logo',
                site: site.id,
                organization: organizationId,
                accept: 'images',
            },
            { loading: 'Uploading logo…', success: 'Logo uploaded' }
        );
        if (metadata && isUrl(metadata.downloadURL)) {
            setSettings((prev) => ({
                ...prev,
                footer: {
                    ...prev.footer,
                    logo: setThemedURL(
                        prev.footer?.logo ?? undefined,
                        themeMode,
                        metadata.downloadURL || null
                    ),
                },
            }));

            analytics.logEvent(
                analytics.createSiteEvent(
                    { id: site.id, organization: organizationId },
                    {
                        type: 'site_edit_publish_upload_footer_logo',
                    }
                )
            );
        }
    };

    const removeFooterLogo = () => {
        setSettings((prev) => ({
            ...prev,
            footer: {
                ...prev.footer,
                logo: undefined,
            },
        }));
    };

    return (
        <div className="flex flex-1 flex-col">
            <ImagePreview
                onPress={onPressUpload}
                style={{
                    aspectRatio: '2 / 1',
                }}
            >
                {footerLogoResizedURI ? (
                    <>
                        <ProgressiveImage
                            src={footerLogoResizedURI}
                            className="max-h-full w-full object-contain"
                            rounded={false}
                        />
                        <ImagePreviewRemoveButton onPress={removeFooterLogo} />
                    </>
                ) : (
                    <ImagePreviewIcon />
                )}
            </ImagePreview>
            <div className="mt-2 flex items-center gap-2 text-muted">
                {themeMode === CustomizationThemeMode.Light ? (
                    <>
                        <Icon.ThemeLight size={16} className="shrink-0" />
                        <div className="text-ui-small">Light mode</div>
                    </>
                ) : (
                    <>
                        <Icon.ThemeDark size={16} className="shrink-0" />
                        <div className="text-ui-small">Dark mode</div>
                    </>
                )}
            </div>
        </div>
    );
}
